import type {ReactElement} from 'react';
import {useCallback} from 'react';
import React, {useRef, useEffect} from 'react';
import type {Block, Component} from 'grapesjs';
import {getBlocksFromCategories} from '@Libraries/email-component-template-library';
import {useGrapesjsEditor} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import useOutsideClick from '@Hooks/useOutsideClick';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import PopupMenuItem from '@Components/popup-menu/components/popup-menu-item/popup-menu-item';
import type {AddBlockMenuProps} from './add-block-menu.types';
import {InsertPosition} from './add-block-menu.types';
import styles from './add-block-menu.module.scss';

export {InsertPosition} from './add-block-menu.types';

const getDefaultBlockIdsToRender = (): string[] => {
  return [
    window.PMW.emails.blocks.ID_TEXT_PRESET_1,
    window.PMW.emails.blocks.ID_UPLOAD_CONTENT,
    window.PMW.emails.blocks.ID_BUTTON,
    window.PMW.emails.blocks.ID_SECTION_PRESET_2,
    window.PMW.emails.blocks.ID_DIVIDER,
    window.PMW.emails.blocks.ID_STICKER,
    window.PMW.emails.blocks.ID_VIDEO,
    window.PMW.emails.blocks.ID_SOCIAL,
    window.PMW.emails.blocks.ID_TABLE,
  ];
};

export function AddBlockMenu({insertPosition, blockIdsToRender}: AddBlockMenuProps): ReactElement {
  const gjsEditor = useGrapesjsEditor();
  const menuRef = useRef<HTMLDivElement>(null);

  const blocksToRender: string[] = blockIdsToRender ?? getDefaultBlockIdsToRender();

  const BLOCK_CATEGORY = [window.PMW.emails.blocks.CATEGORY_ELEMENTS];

  const closeMenu = useCallback((): void => {
    const container = document.querySelector('.pmw-add-element-menu-container');
    if (container) {
      container.remove();
    }
  }, []);

  useOutsideClick(menuRef, closeMenu);

  useEffect(() => {
    const container = document.querySelector('.pmw-add-element-menu-container');
    if (!container) {
      return;
    }

    const frameEl = gjsEditor.Canvas.getFrameEl();
    if (!frameEl?.contentWindow) {
      return;
    }

    const iframeDocument = frameEl.contentDocument ?? frameEl.contentWindow.document;

    const handleIframeClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        const plusButton = (e.target as HTMLElement).closest('.pmw-add-element-button');
        if (!plusButton) {
          closeMenu();
        }
      }
    };

    const updateMenuPosition = (): void => {
      const button = iframeDocument.querySelector('.pmw-add-element-button.-active');
      if (!button || !container) {
        return;
      }

      const rect = button.getBoundingClientRect();
      const frameRect = frameEl.getBoundingClientRect();
      const position = button.classList.contains('top') ? 'above' : 'below';

      const top = position === 'above' ? rect.top : rect.bottom;
      const left = rect.left;

      (container as HTMLElement).style.top = `${frameRect.top + top + window.scrollY}px`;
      (container as HTMLElement).style.left = `${frameRect.left + left + window.scrollX}px`;
    };

    const handleIframeScroll = (): void => {
      updateMenuPosition();
    };

    iframeDocument.addEventListener('click', handleIframeClick, {capture: true});
    iframeDocument.addEventListener('scroll', handleIframeScroll, true);

    return (): void => {
      iframeDocument.removeEventListener('click', handleIframeClick, {capture: true});
      iframeDocument.removeEventListener('scroll', handleIframeScroll, true);
    };
  }, [gjsEditor, closeMenu]);

  const getBlockLabel = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return window.i18next.t('pmwjs_table');
      case window.PMW.emails.blocks.ID_STICKER:
        return window.i18next.t('pmwjs_sticker');
      case window.PMW.emails.blocks.ID_VIDEO:
        return window.i18next.t('pmwjs_video');
      case window.PMW.emails.blocks.ID_BUTTON:
        return window.i18next.t('pmwjs_button');
      case window.PMW.emails.blocks.ID_SOCIAL:
        return window.i18next.t('pmwjs_social');
      case window.PMW.emails.blocks.ID_DIVIDER:
        return window.i18next.t('pmwjs_divider');
      case window.PMW.emails.blocks.ID_TEXT_PRESET_1:
        return window.i18next.t('pmwjs_text');
      case window.PMW.emails.blocks.ID_TEXT_PRESET_2:
        return window.i18next.t('pmwjs_title');
      case window.PMW.emails.blocks.ID_UPLOAD_CONTENT:
        return window.i18next.t('pmwjs_images');
      case window.PMW.emails.blocks.ID_EXPLORE_MEDIA:
        return window.i18next.t('pmwjs_explore_media');
      case window.PMW.emails.blocks.ID_AI_IMAGES:
        return window.i18next.t('pmwjs_ai_images');
      case window.PMW.emails.blocks.ID_SECTION_PRESET_2:
        return window.i18next.t('pmwjs_section');
      default:
        return 'Block label not found';
    }
  };

  const getBlockIcon = (blockId: string): string => {
    switch (blockId) {
      case window.PMW.emails.blocks.ID_TABLE:
        return 'icon-table';
      case window.PMW.emails.blocks.ID_STICKER:
        return 'icon-sticker';
      case window.PMW.emails.blocks.ID_VIDEO:
        return 'icon-video';
      case window.PMW.emails.blocks.ID_BUTTON:
        return 'icon-button';
      case window.PMW.emails.blocks.ID_SOCIAL:
        return 'icon-social-media';
      case window.PMW.emails.blocks.ID_DIVIDER:
        return 'icon-divider';
      case window.PMW.emails.blocks.ID_SECTION_PRESET_2:
        return 'icon-section';
      case window.PMW.emails.blocks.ID_UPLOAD_CONTENT:
      case window.PMW.emails.blocks.ID_EXPLORE_MEDIA:
      case window.PMW.emails.blocks.ID_AI_IMAGES:
      case window.PMW.emails.blocks.CATEGORY_IMAGE:
        return 'icon-image';
      case window.PMW.emails.blocks.ID_DESIGN:
        return 'icon-designs';
      case window.PMW.emails.blocks.CATEGORY_TEXT:
        return 'icon-text';
      case window.PMW.emails.blocks.ID_TEXT_PRESET_1:
      case window.PMW.emails.blocks.ID_TEXT_PRESET_2:
        return 'icon-text';
      default:
        return 'icon-square';
    }
  };

  const isHtmlContentWithinLimit = (): boolean => {
    const htmlContent = gjsEditor.runCommand(window.PMW.emails.commands.GET_INLINED_HTML) as string;
    return htmlContent.length <= window.PMW.emails.MAX_HTML_CONTENT_LENGTH;
  };

  const getParentComponent = (selected: Component): Component | undefined => {
    let parent = selected.parent();
    if (!parent) {
      const wrapperComponents = gjsEditor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_WRAPPER);
      parent = wrapperComponents?.[0];
    }
    return parent;
  };

  const convertContentForCanMoveCheck = (content: unknown): string => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const contentForCheck = typeof content === 'function' ? content() : content;
    return Array.isArray(contentForCheck) ? JSON.stringify(contentForCheck) : (contentForCheck as string);
  };

  const findSuitableParent = (initialParent: Component, selected: Component, contentAsString: string): {targetParent: Component; targetComponent: Component} | null => {
    let targetParent: Component | undefined = initialParent;
    let targetComponent = selected;
    let canMoveResult = gjsEditor.Components.canMove(targetParent, contentAsString);

    while (!canMoveResult.result && targetParent) {
      targetComponent = targetParent;
      targetParent = targetParent.parent();

      if (!targetParent) {
        return null;
      }

      canMoveResult = gjsEditor.Components.canMove(targetParent, contentAsString);
    }

    if (!canMoveResult.result || !targetParent) {
      return null;
    }

    return {targetParent, targetComponent};
  };

  const insertComponentAtPosition = (targetParent: Component, targetComponent: Component, content: unknown): Component | undefined => {
    const selectedIndex = targetParent.components().indexOf(targetComponent);
    const insertAt = insertPosition === InsertPosition.ABOVE ? selectedIndex : selectedIndex + 1;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    return targetParent.append(content as any, {at: insertAt})?.[0];
  };

  const addBlockWithPosition = (block: Block): void => {
    const content = block.get('content');
    const selected = gjsEditor.getSelected();

    if (!selected || !content) {
      return;
    }

    if (!isHtmlContentWithinLimit()) {
      return;
    }

    const parent = getParentComponent(selected);
    if (!parent) {
      return;
    }

    const contentAsString = convertContentForCanMoveCheck(content);
    const suitableParent = findSuitableParent(parent, selected, contentAsString);

    if (!suitableParent) {
      return;
    }

    const result = insertComponentAtPosition(suitableParent.targetParent, suitableParent.targetComponent, content);

    if (result) {
      addMediaQueries(result);
      applyStylesFromSelectedComponent(selected, result);
      gjsEditor.select(result, {scroll: true});
    }
  };

  const addMediaQueries = (component: Component): void => {
    const responsiveStyles = ['width'];
    gjsEditor.runCommand(window.PMW.emails.commands.REFRESH_MEDIA_QUERIES, {
      component,
      responsiveStyles: responsiveStyles,
    });
  };

  const applyStylesFromSelectedComponent = (sourceComponent: Component, targetComponent: Component): void => {
    const sourceType = sourceComponent.get('type');

    if (!sourceType) {
      return;
    }

    gjsEditor.runCommand(window.PMW.emails.commands.COPY_STYLES, {component: sourceComponent});

    const targetType = targetComponent.get('type');
    if (targetType && targetType === sourceType) {
      gjsEditor.runCommand(window.PMW.emails.commands.PASTE_STYLES, {component: targetComponent});
      return;
    }

    const matchingComponents = targetComponent.findType(sourceType);
    matchingComponents.forEach((matchingComp: Component) => {
      gjsEditor.runCommand(window.PMW.emails.commands.PASTE_STYLES, {component: matchingComp});
    });
  };

  const handleBlockClick = (block: Block): void => {
    addBlockWithPosition(block);
    closeMenu();
  };

  const getAllBlocks = (): Block[] => {
    const allBlocks = gjsEditor.BlockManager.getAll();
    const blocksMap: Record<string, Block[]> = {};

    allBlocks.forEach((block: Block) => {
      if (!block) {
        return;
      }
      const category = block.get('category') as {id: string} | string;
      const categoryId = typeof category === 'string' ? category : category?.id;
      if (!categoryId) {
        return;
      }
      if (!blocksMap[categoryId]) {
        blocksMap[categoryId] = [];
      }
      blocksMap[categoryId].push(block);
    });

    const elementBlocks: Block[] = getBlocksFromCategories(blocksMap, BLOCK_CATEGORY);
    const textBlocks: Block[] = blocksMap[window.PMW.emails.blocks.CATEGORY_TEXT] ?? [];
    const imageBlocks: Block[] = blocksMap[window.PMW.emails.blocks.CATEGORY_IMAGE] ?? [];
    const sectionBlocks: Block[] = blocksMap[window.PMW.emails.blocks.CATEGORY_SECTIONS] ?? [];

    const allBlocksArray = [...textBlocks, ...imageBlocks, ...elementBlocks, ...sectionBlocks];

    const filteredBlocks = allBlocksArray.filter((block) => {
      if (!block) {
        return false;
      }
      const blockId = block.getId();
      return blocksToRender.includes(blockId);
    });

    filteredBlocks.sort((block1, block2) => {
      const index1 = blocksToRender.indexOf(block1.getId());
      const index2 = blocksToRender.indexOf(block2.getId());
      return index1 - index2;
    });

    return filteredBlocks;
  };

  const renderPopupContent = (): ReactElement => {
    return (
      <div className={styles.menuContainer}>
        <div className={styles.heading}>{window.i18next.t('pmwjs_add_elements')}</div>
        <ul className={styles.menuList}>
          {getAllBlocks().map((block) => {
            const blockId = block.getId();
            const iconName = getBlockIcon(blockId);

            return (
              <PopupMenuItem
                key={blockId}
                text={getBlockLabel(blockId)}
                itemClasses={styles.menuListItem}
                iconClasses={iconName}
                clickHandler={(): void => {
                  handleBlockClick(block);
                }}
                textTypographyClasses="body-xs"
                textColorClasses="content-heading"
                iconColorClasses="content-heading"
              />
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div ref={menuRef}>
      <Dropdown
        open
        popup={renderPopupContent()}
        popupClassName={styles.dropdownPopup}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        position={DROPDOWN_POSITION.FIXED}
        doCloseOnInnerClick
        doesCloseOnOuterClick
        shouldFlip
        shouldShift
        popUpHasCustomWidth
        popUpHasCustomHeight
        showModalForSmallScreen
        maxHeight={400}
        popupOffset={8}
      />
    </div>
  );
}
